import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";

export enum ScrollState {
  NONE = 0,
  REDUCED = 1,
  FULL = 2,
}

const useHeightObserver = (element: HTMLElement | null, setAnimated: Dispatch<SetStateAction<boolean>>) => {
  const [height, setHeight] = useState(element?.offsetHeight ?? 0);

  useEffect(() => {
    if (element) {
      const resizeObserver = new ResizeObserver(() => {
        setHeight(element.offsetHeight);
        setAnimated(false);
      });
      resizeObserver.observe(element);
      return () => resizeObserver.disconnect();
    }
  }, [element, setAnimated]);

  return height;
};

export const QUICK_COLLAPSE_THRESHOLD = 10;

export interface IResponsiveScrollOptions {
  collapseThreshold?: number;
}

export const useResponsiveScroll = (
  scroller: HTMLElement | null,
  mainContainer: HTMLElement | null,
  subContainer: HTMLElement | null,
  staticContainer: HTMLElement | null,
  options?: IResponsiveScrollOptions
) => {
  const [scrollState, setScrollState] = useState(ScrollState.FULL);
  const [animated, setAnimated] = useState(false);
  const staticContainerHeight = useHeightObserver(staticContainer, setAnimated);
  const mainContainerHeight = useHeightObserver(mainContainer, setAnimated);
  const subContainerHeight = useHeightObserver(subContainer, setAnimated);
  const containersHeightsRef = useRef({ mainContainerHeight, subContainerHeight, staticContainerHeight });
  containersHeightsRef.current.mainContainerHeight = mainContainerHeight;
  containersHeightsRef.current.subContainerHeight = subContainerHeight;
  containersHeightsRef.current.staticContainerHeight = staticContainerHeight;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (scroller) {
      let prevScrollTop = 0;
      const listener = () => {
        const threshold =
          optionsRef.current?.collapseThreshold ??
          containersHeightsRef.current.mainContainerHeight - containersHeightsRef.current.staticContainerHeight;
        if (scroller.scrollTop < threshold) {
          setScrollState(ScrollState.FULL);
        } else if (prevScrollTop < scroller.scrollTop) {
          setScrollState(ScrollState.NONE);
        } else if (prevScrollTop > scroller.scrollTop) {
          setScrollState(ScrollState.REDUCED);
        } else {
          return;
        }
        setAnimated(true);
        prevScrollTop = scroller.scrollTop;
      };
      scroller.addEventListener("scroll", listener);
      return () => scroller.removeEventListener("scroll", listener);
    }
  }, [scroller]);

  return {
    animated,
    mainContainerHeight,
    scaleY:
      scrollState === ScrollState.FULL
        ? 1
        : scrollState === ScrollState.REDUCED
          ? (subContainerHeight + staticContainerHeight) / mainContainerHeight
          : staticContainerHeight / mainContainerHeight,
    collapseHeight:
      scrollState === ScrollState.FULL
        ? mainContainerHeight
        : scrollState === ScrollState.REDUCED
          ? subContainerHeight + staticContainerHeight
          : staticContainerHeight,
  };
};
